import L, {TileLayer, LatLngLiteral, Map as LMap, Marker} from 'leaflet';
import 'leaflet/dist/leaflet.css';
import 'leaflet-defaulticon-compatibility';
import 'leaflet-defaulticon-compatibility/dist/leaflet-defaulticon-compatibility.css';

enum Zip {
	Liberec = 46001,
	Jablonec = 46601
}

interface Participant {
	place: string;
	zip: number;
	count: number;
	position: LatLngLiteral | null;
}

interface MediaMap {
	maxMedia: string,
	center: LatLngLiteral,
	zoom: number,
	minZoom: number,
	maxZoom: number,
}

interface MapConfiguration {
	center: LatLngLiteral & {
		asMarker?: boolean,
	},
	zoom: number,
	minZoom?: number,
	maxZoom: number,
	markers?: Participant[],
	media: MediaMap[],
}

export class Map {
	private instances: Array<{
		mapInstance: LMap,
	}> = [];

	constructor(
					private mapSelector: string,
	) {
		this.init();
	}

	private init(): void {
		document.querySelectorAll<HTMLElement>(this.mapSelector)
						.forEach(mapElement => {
							const configScript = mapElement.querySelector<HTMLScriptElement>('script');
							const mapContainer = mapElement.querySelector<HTMLDivElement>('.map-container');
							const markerPopup = mapElement.querySelector<HTMLDivElement>('.marker-popup');

							const mapConfig = JSON.parse(configScript.textContent) as MapConfiguration;

							let mapOptions = {
								center: {
									lat: mapConfig.center.lat,
									lng: mapConfig.center.lng,
								},
								zoom: mapConfig.zoom,
								minZoom: mapConfig.minZoom,
								maxZoom: mapConfig.maxZoom,
							};

							// Apply the first matching media config override, if any
							if (Array.isArray(mapConfig.media)) {
								const matched = mapConfig.media.find(
												media => window.matchMedia(`(max-width: ${media.maxMedia})`).matches
								);

								if (matched) {
									mapOptions = {
										center: matched.center,
										zoom: matched.zoom,
										minZoom: matched.minZoom,
										maxZoom: matched.maxZoom,
									};
								}
							}

							const mapInstance = new LMap(
											mapContainer,
											{
												... mapOptions,
												scrollWheelZoom: false,
											},
							);

							new TileLayer('https://tile.openstreetmap.org/{z}/{x}/{y}.png', {
								attribution: '&copy; <a href="http://www.openstreetmap.org/copyright">OpenStreetMap</a>'
							}).addTo(mapInstance);

							if (mapConfig.center.asMarker === true) {
								const marker = new Marker(mapOptions.center).addTo(mapInstance);

								if (markerPopup !== undefined) {
									marker.bindPopup(markerPopup.innerHTML);
									marker.openPopup();
								}
							}

							this.renderMarkers(mapConfig, mapInstance);

							this.instances.push({
								mapInstance,
							});
						});
	}

	private renderMarkers(
					mapConfig: MapConfiguration,
					mapInstance: LMap,
	) :void {

		const markerAtSinglePlace = mapConfig.markers
						?.filter(marker => marker.zip !== Zip.Liberec && marker.zip !== Zip.Jablonec)
						.reduce((acc, curr) => curr.count > acc ? curr.count : acc, 0);

		if (markerAtSinglePlace === 0 || markerAtSinglePlace === undefined) {
			return;
		}

		let marker: L.Marker[] = [];
		let circle: L.Circle[] = [];
		const maxRadius = 10000; // Nove bude 15000 jen Liberec, 12000 Jablonec
		const minRadius = maxRadius / 10; // default 5

		mapConfig.markers.map((data, i) => {
			marker[i] = L.marker(data.position, {
				title: data.count.toString()
			});
			marker[i].addTo(mapInstance);

			const divEl = document.createElement('div');

			const pEl = document.createElement('p');
			pEl.style.textAlign = 'left';

			const strongEl = document.createElement('strong');
			strongEl.innerHTML = data.place;

			const tableEl = document.createElement('table');
			const trEl = document.createElement('tr');
			const td1El = document.createElement('td');
			td1El.style.textAlign = 'right';
			td1El.innerHTML = 'účast na kurzech LŠF: ';

			const td2El = document.createElement('td');
			td2El.style.textAlign = 'right';
			td2El.style.paddingLeft = '1em';

			const strongTd2El = document.createElement('strong');
			strongTd2El.innerHTML = data.count.toString();

			divEl.appendChild(pEl);
			pEl.appendChild(strongEl);

			divEl.appendChild(tableEl);
			tableEl.appendChild(trEl);
			trEl.appendChild(td1El);
			trEl.appendChild(td2El);
			td2El.appendChild(strongTd2El);

			let radius = (data.count / markerAtSinglePlace) * maxRadius;

			if (data.zip === Zip.Liberec) {
				radius = 15000;
			} else if (data.zip === Zip.Jablonec) {
				radius = 12000;
			} else if (radius < minRadius) {
				radius = minRadius;
			}

			circle[i] = L.circle(data.position, {radius: radius});
			circle[i].addTo(mapInstance);

			marker[i].bindPopup(divEl, {
				autoPan: false
			});
			circle[i].on('click', () => {
				marker[i].togglePopup();
			});
		});
	}

	private tearDown(): void {
		for (const {mapInstance} of this.instances) {
			mapInstance.remove();
		}

		this.instances = [];
	}
}

export let map: Map | undefined;

document.addEventListener('DOMContentLoaded', () => {
	map = new Map('.map');
});
